import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { observer } from "mobx-react";
import { useToast } from "../../../../../components/toast";
import { Alert } from "@ann/web-components";
import {
  MyWorkspaceContainer,
  FiltersRow,
  ContentArea,
} from "./MyWorkspace.styled";
import Filters from "./components/Filters/Filters";
import { useDrivesStores } from "../../hook/use-drives.hook";
import { useStore } from "../../../../../hooks";
import { useStoreNotifications } from "../../hook/use-store-notifications.hook";
import { useLoadMorePagination } from "../../hooks/useLoadMorePagination.js";
import { useFileUpload } from "../../hook/use-file-upload.hook";

import ShareModal from "./components/Modals/ShareModal";
import DuplicateModal from "./components/Modals/DuplicateModal";
import AssignAgentModal from "./components/Modals/AssignAgentModal";
import FoldersList from "./components/FoldersList";
import DocumentsList from "./components/DocumentsList";
import RenameModal from "../../components/RenameModal";
import RenameFolderModal from "../../components/RenameFolderModal";
import RenameDocumentModal from "../../components/RenameDocumentModal/RenameDocumentModal";
import UnifiedTable from "../../components/UnifiedTable";
import DocumentDetailSlideout from "../../components/DocumentDetailSlideout/DocumentDetailSlideout";
import MoveFolderModal from "../../components/MoveFolderModal";
import FolderInformationSlideout from "../../components/FolderInformationSlideout/FolderInformationSlideout";
import { BannerSelected } from "../../components/BannerSelected";
import DeleteDocumentModal from "../../components/DeleteDocumentModal";

const CLOSE_DELAY_MS = 170;

const MyWorkspace = () => {
  const { myWorkspacesStore, selectionStore, drivesViewStore } = useDrivesStores();
  const { resourcesStore } = myWorkspacesStore;
  const toast = useToast();
  const { authStore, documentsStore } = useStore();
  const userId = authStore.userId;

  // Hook for file upload handling (use rootFolderUuid when at My Workspace root)
  const handleUploadComplete = useCallback(async () => {
    await myWorkspacesStore.loadWorkspaceData();
    setFolders(myWorkspacesStore.folderList || []);
    setDocuments(myWorkspacesStore.documentList || []);
  }, [myWorkspacesStore]);

  const { fileInputRef, fileInputProps, handleDrop, openFileSelector } = useFileUpload({
    folderId: myWorkspacesStore.rootFolderUuid,
    onUploadComplete: handleUploadComplete,
  });

  // ✅ Local state for data
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [viewMode, setViewMode] = useState("grid");

  // ✅ Local state for document detail slideout
  const [isDocumentDetailOpen, setIsDocumentDetailOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [isFolderInfoOpen, setIsFolderInfoOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);

  // ✅ State for RenameFolderModal
  const [isRenameFolderModalOpen, setIsRenameFolderModalOpen] = useState(false);
  const [folderToRename, setFolderToRename] = useState(null);

  // ✅ State for RenameDocumentModal
  const [isRenameDocumentModalOpen, setIsRenameDocumentModalOpen] = useState(false);
  const [documentToRename, setDocumentToRename] = useState(null);

  const [alert, setAlert] = useState(null);


  // ✅ PERFORMANCE: Load data once and store in local state
  // Wait for drives to be loaded before loading workspace data
  const workspaceDataLoaded = useRef(false);
  const isLoadingWorkspace = useRef(false); // ✅ Prevent concurrent loads
  
  useEffect(() => {
    const loadData = async () => {
      // Only load once when drives become available
      const hasDrives = drivesViewStore?.drivesStore?.drives?.length > 0;
      
      // ✅ FIX: Prevent loading if already loaded or currently loading
      if (hasDrives && !workspaceDataLoaded.current && !isLoadingWorkspace.current) {
        isLoadingWorkspace.current = true;
        try {
          await myWorkspacesStore.loadWorkspaceData();
          // ✅ REMOVED: Lazy load users and privileges only when Share modal opens
          // await myWorkspacesStore.usersStore.fetchUsersFilters();
          // Sync data to local state
          setFolders(myWorkspacesStore.folderList || []);
          setDocuments(myWorkspacesStore.documentList || []);
          workspaceDataLoaded.current = true;
        } finally {
          isLoadingWorkspace.current = false;
        }
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myWorkspacesStore, drivesViewStore?.drivesStore?.drives?.length]); // ✅ FIX: Usar .length en lugar del array completo

  // Sync local state when store data changes
  useEffect(() => {
    setFolders(myWorkspacesStore.folderList || []);
    setDocuments(myWorkspacesStore.documentList || []);
  }, [myWorkspacesStore.folderList, myWorkspacesStore.documentList]);

  // ✅ Actualizar selectedDocument cuando cambia en el store
  useEffect(() => {
    if (selectedDocument) {
      const documentUuid = selectedDocument.document_uuid || selectedDocument.documentUuid || selectedDocument.id;
      const updatedDoc = myWorkspacesStore.documentList?.find(
        (doc) =>
          doc.document_uuid === documentUuid ||
          doc.documentUuid === documentUuid ||
          doc.id === documentUuid
      );
      if (updatedDoc && updatedDoc.name !== selectedDocument.name) {
        setSelectedDocument(updatedDoc);
      }
    }
  }, [myWorkspacesStore.documentList, selectedDocument]);

  // Expose file selector function
  useEffect(() => {
    myWorkspacesStore.setFileInputRef(fileInputRef);
  }, [myWorkspacesStore, fileInputRef]);

  // ✅ Toast notifications for Folder and Upload operations
  useStoreNotifications([
    {
      condition: myWorkspacesStore?.foldersStore?.createdFolder,
      type: 'success',
      description: `${myWorkspacesStore?.foldersStore?.folderCreated?.name} was created`,
      onClose: () => myWorkspacesStore?.foldersStore?.setCreatedFolder(false)
    },
    {
      condition: myWorkspacesStore?.foldersStore?.folderAlreadyExists,
      type: 'error',
      title: "A folder with this name already exists.",
      description: "Please choose a different name.",
      onClose: () => myWorkspacesStore?.foldersStore?.setFolderAlreadyExists(false)
    },
    {
      condition: myWorkspacesStore?.foldersStore?.isErrorCreateFolder,
      type: 'error',
      title: "Connection error, folder could not be created.",
      description: "Please try again or check your connection.",
      onClose: () => myWorkspacesStore?.foldersStore?.setIsErrorCreateFolder(false)
    },
    {
      condition: documentsStore?.uploadSuccess,
      type: 'success',
      title: "Upload successful",
      description: "Your documents have been uploaded successfully",
      onClose: () => documentsStore?.setUploadSuccess(false)
    },
    {
      condition: documentsStore?.uploadFailed,
      type: 'error',
      title: "Upload failed",
      description: documentsStore?.errorMessage || "Some files failed to upload",
      onClose: () => {
        documentsStore?.setUploadFailed(false);
        documentsStore?.clearErrorMessage();
      }
    }
  ]);

  // ✅ PERFORMANCE: Filter and sort locally with useMemo
  const filteredAndSortedFolders = useMemo(() => {
    let filtered = folders;

    // Filtrar si hay texto de búsqueda
    if (filterText) {
      filtered = filtered.filter((folder) =>
        folder.name.toLowerCase().includes(filterText.toLowerCase())
      );
      
      // Solo ordenar si hay filtro aplicado
      return filtered.slice().sort((a, b) => {
        const direction = sortDirection === "asc" ? 1 : -1;

        switch (sortBy) {
          case "name":
            return direction * a.name.localeCompare(b.name);
          case "lastUpdated":
            return direction * (new Date(a.updated_at) - new Date(b.updated_at));
          default:
            return 0;
        }
      });
    }

    // Si NO hay filtro, NO ordenar - mantener orden del backend
    return filtered;
  }, [folders, filterText, sortBy, sortDirection]);

  const filteredAndSortedDocuments = useMemo(() => {
    let filtered = documents;

    // Filtrar si hay texto de búsqueda
    if (filterText) {
      filtered = filtered.filter((doc) =>
        doc.name.toLowerCase().includes(filterText.toLowerCase())
      );
      
      // Solo ordenar si hay filtro aplicado
      return filtered.slice().sort((a, b) => {
        const direction = sortDirection === "asc" ? 1 : -1;

        switch (sortBy) {
          case "name":
            return direction * a.name.localeCompare(b.name);
          case "lastUpdated":
            return direction * (new Date(a.updated_at) - new Date(b.updated_at));
          default:
            return 0;
        }
      });
    }

    // Si NO hay filtro, NO ordenar - mantener orden del backend
    return filtered;
  }, [documents, filterText, sortBy, sortDirection]);

  // ✅ Pagination hooks con soporte para backend
  const {
    visibleItems: visibleFolders,
    hasMore: hasMoreFolders,
    isLoading: isLoadingFolders,
    loadMore: loadMoreFolders,
  } = useLoadMorePagination(
    filteredAndSortedFolders, 
    8, 
    2000,
    myWorkspacesStore.foldersHasMore, // backendHasMore
    () => myWorkspacesStore.loadMoreFolders(), // onLoadMoreFromBackend
    myWorkspacesStore.isLoadingMoreFolders // isLoadingFromBackend
  );

  const {
    visibleItems: visibleDocuments,
    hasMore: hasMoreDocuments,
    isLoading: isLoadingDocuments,
    loadMore: loadMoreDocuments,
  } = useLoadMorePagination(
    filteredAndSortedDocuments, 
    8, 
    2000,
    myWorkspacesStore.documentsHasMore, // backendHasMore
    () => myWorkspacesStore.loadMoreDocuments(), // onLoadMoreFromBackend
    myWorkspacesStore.isLoadingMoreDocuments // isLoadingFromBackend
  );

  // Event handlers
  // Toast helpers (igual que tenías)
  const handleShareModalOpen = (item, type) => {
    const result = myWorkspacesStore.openShareModal(item, type);
    if (result?.message) {
      toast[result.variant]({
        title: result.title,
        message: result.message,
      });
    }
  };

  const handleBannerShare = () => {
    const selectedDocuments =
      myWorkspacesStore.selectionStore.selectedDocuments;
    const selectedFolders = myWorkspacesStore.selectionStore.selectedFolders;

    if (selectedDocuments.length > 0 || selectedFolders.length > 0) {
      const virtualItem = {
        type: selectedFolders.length > 0 ? "folder" : "document",
        name: `${
          selectedDocuments.length + selectedFolders.length
        } items selected`,
      };

      handleShareModalOpen(virtualItem, "mixed");
    }
  };

  const handleBannerMove = () => {
    myWorkspacesStore.openMoveModal();
  };
  
  const handleAddToPending = () => {
    const result = myWorkspacesStore.addToPendingGrantees();
    if (result?.message) {
      toast[result.variant]({
        title: result.title,
        message: result.message,
      });
    }
  };
  const handleShareAll = async () => {
    const result = await myWorkspacesStore.shareAllPendingGrantees();
    if (result?.message) {
      toast[result.variant]({
        title: result.title,
        message: result.message,
      });
    }
  };
  const handleRemoveAccess = async (granteeUuid, granteeType) => {
    const result = await myWorkspacesStore.removeAccessFromItem(
      granteeUuid,
      granteeType
    );

    // Siempre mostrar toast con el resultado
    if (result) {
      const variant = result.variant || (result.success ? 'success' : 'error');
      const title = result.title || (result.success ? 'Success' : 'Error');
      const message = result.message || (result.success ? 'Operation completed' : 'Operation failed');
      
      toast[variant]({
        title: title,
        message: message,
      });
    }
  };

  const handleUpdatePermission = async (
    granteeUuid,
    granteeType,
    newPermission
  ) => {
    const result = await myWorkspacesStore.updatePermissionForGrantee(
      granteeUuid,
      granteeType,
      newPermission
    );
    
    // Siempre mostrar toast con el resultado
    if (result) {
      const variant = result.variant || (result.success ? 'success' : 'error');
      const title = result.title || (result.success ? 'Success' : 'Error');
      const message = result.message || (result.success ? 'Operation completed' : 'Operation failed');
      
      toast[variant]({
        title: title,
        message: message,
      });
    }
  };
  const handleDuplicateItem = async () => {
    const result = await myWorkspacesStore.duplicateItem();
    if (result?.message) {
      toast[result.variant]({
        title: result.title,
        message: result.message,
      });
    }
  };
  const handleAssignAgentModalOpen = (item) => {
    const result = myWorkspacesStore.openAssignAgentModal(item);
    if (result?.message) {
      toast[result.variant]({
        title: result.title,
        message: result.message,
      });
    }
  };
  const handleAssignAgent = async () => {
    const result = await myWorkspacesStore.assignAgentsToDocument(userId);
    if (result?.message) {
      toast[result.variant]({
        title: result.title,
        message: result.message,
      });
    }
  };

  const handleOpenDocumentDetail = (document) => {
    setSelectedDocument(document);
    setIsDocumentDetailOpen(true);
  };

  const handleOpenFolderInfo = (folder) => {
    setSelectedFolder(folder);
    setIsFolderInfoOpen(true);
  };

  const closeSlideoutThen = (next) => {
    setIsDocumentDetailOpen(false);
    setTimeout(next, CLOSE_DELAY_MS);
  };

  // ✅ Handlers for RenameFolderModal
  const handleOpenRenameFolderModal = (folder) => {
    setFolderToRename(folder);
    setIsRenameFolderModalOpen(true);
  };

  const handleCloseRenameFolderModal = () => {
    setIsRenameFolderModalOpen(false);
    setFolderToRename(null);
  };

  const handleSaveRenameFolder = (result) => {
    if (result.success && result.updatedFolder) {
      // Update folder with data from API
      const folder = myWorkspacesStore.folderList.find(f => f.folder_uuid === result.folderUuid);
      if (folder) {
        folder.setName(result.updatedFolder.name);
        myWorkspacesStore.setFolderInList(folder);
      }
    }
    // Toast is handled by the modal
  };

  // ✅ Handlers for RenameDocumentModal
  const handleOpenRenameDocumentModal = (document) => {
    setDocumentToRename(document);
    setIsRenameDocumentModalOpen(true);
  };

  const handleCloseRenameDocumentModal = () => {
    setIsRenameDocumentModalOpen(false);
    setDocumentToRename(null);
  };

  const handleSaveRenameDocument = (result) => {
    if (result?.success) {
      const newName = result.newName || result.updatedDocument?.name;
      const documentUuid =
        result.documentUuid ||
        result.updatedDocument?.documentUuid ||
        result.updatedDocument?.document_uuid ||
        documentToRename?.document_uuid ||
        documentToRename?.documentUuid ||
        documentToRename?.id;

      if (documentUuid && newName) {
        const updateDocumentName = (doc) => {
          if (doc && typeof doc.setName === 'function') {
            doc.setName(newName);
          } else if (doc) {
            doc.name = newName;
          }
        };

        const docInStore = myWorkspacesStore.documentList.find(
          (doc) =>
            doc.document_uuid === documentUuid ||
            doc.documentUuid === documentUuid ||
            doc.id === documentUuid
        );

        if (docInStore) {
          updateDocumentName(docInStore);
          myWorkspacesStore.setDocumentInList(docInStore);
        }
        // ✅ REMOVED: No need to manually update local state
        // The useEffect will sync automatically from the store
      }
    }
  };

  const handleDeleteDocument = useCallback((document) => {
    myWorkspacesStore.openDeleteModal(document);
  }, [myWorkspacesStore]);

  const handleConfirmDelete = useCallback(async () => {
    let result;
    try {
      result = await myWorkspacesStore.deleteDocument();
    } catch (error) {
      result = { success: false, error: error.message || 'Unexpected error deleting document' };
    }
    
    myWorkspacesStore.closeDeleteModal();
    
    setTimeout(() => {
      if (result.success) {
        setAlert({
          title: "Success",
          message: result.message || "Document deleted successfully",
          severity: "success"
        });
      } else {
        setAlert({
          title: "Error",
          message: result.error || result.message || 'Error deleting document',
          severity: "error"
        });
      }
    }, 100);
  }, [myWorkspacesStore]);

  const handleCloseAlert = useCallback(() => {
    setAlert(null);
  }, []);

  // ✅ Handle sort change
  const handleSortChange = useCallback(
    (field) => {
      if (sortBy === field) {
        setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(field);
        setSortDirection("asc");
      }
    },
    [sortBy]
  );

  return (
    <>
      {alert && (
        <Alert
          title={alert.message}
          severity={alert.severity}
          onClose={handleCloseAlert}
          maxWidth="400px"
          closable={true}
          autoClose={true}
          autoCloseDelay={3000}
        />
      )}

      {/* Hidden input para selección de archivos */}
      <input {...fileInputProps} ref={fileInputRef} />

      <MyWorkspaceContainer>
        <RenameModal store={myWorkspacesStore.renameDocumentModalStore} />
        <RenameFolderModal 
          isOpen={isRenameFolderModalOpen}
          onClose={handleCloseRenameFolderModal}
          onSave={handleSaveRenameFolder}
          folderUuid={folderToRename?.folder_uuid}
          initialName={folderToRename?.name || ''}
        />

        <RenameDocumentModal
          isOpen={isRenameDocumentModalOpen}
          onClose={handleCloseRenameDocumentModal}
          onSave={handleSaveRenameDocument}
          documentUuid={
            documentToRename?.document_uuid ||
            documentToRename?.documentUuid ||
            documentToRename?.id
          }
          initialName={documentToRename?.name || ''}
        />
        {myWorkspacesStore.selectionStore.countSelectedDocuments === 0 &&
          myWorkspacesStore.selectionStore.countSelectedFolders === 0 && (
            <FiltersRow>
              <Filters
                onFilterChange={setFilterText}
                onSortChange={handleSortChange}
                sortBy={sortBy}
                sortDirection={sortDirection}
                filterText={filterText}
              />
            </FiltersRow>
          )}
        <BannerSelected store={myWorkspacesStore} onShare={handleBannerShare} onMove={handleBannerMove} />
        {myWorkspacesStore.viewMode === "table" ? (
          <UnifiedTable
            folders={visibleFolders}
            documents={visibleDocuments}
            selectionStore={myWorkspacesStore.selectionStore}
            onShare={handleShareModalOpen}
            onDuplicate={myWorkspacesStore.openDuplicateModal}
            onAssignAgent={handleAssignAgentModalOpen}
            onRenameFolder={handleOpenRenameFolderModal}
            onRenameDocument={handleOpenRenameDocumentModal}
            onFolderInfo={handleOpenFolderInfo}
            onMove={(itemId, type) => myWorkspacesStore.openMoveModal(itemId, type)}
            onDelete={handleDeleteDocument}
            hasMore={hasMoreDocuments}
            isLoading={isLoadingDocuments}
            onLoadMore={loadMoreDocuments}
          />
        ) : (
          <ContentArea>
            <>
              <FoldersList
                folders={visibleFolders}
                selectionStore={myWorkspacesStore.selectionStore}
                onShare={handleShareModalOpen}
                onRename={handleOpenRenameFolderModal}
                onDuplicate={myWorkspacesStore.openDuplicateModal}
                onMove={(folderId) =>
                  myWorkspacesStore.openMoveModal(folderId, "folder")
                }
                onFolderInfo={handleOpenFolderInfo}
                onAssignAgent={handleAssignAgentModalOpen}
                hasMore={hasMoreFolders}
                isLoading={isLoadingFolders}
                onLoadMore={loadMoreFolders}
              />

              <DocumentsList
                documents={visibleDocuments}
                selectionStore={myWorkspacesStore.selectionStore}
                onShare={handleShareModalOpen}
                onDuplicate={myWorkspacesStore.openDuplicateModal}
                onRename={handleOpenRenameDocumentModal}
                onMove={(documentId) =>
                  myWorkspacesStore.openMoveModal(documentId, "document")
                }
                onDelete={handleDeleteDocument}
                onAssignAgent={handleAssignAgentModalOpen}
                onOpenDocumentDetail={handleOpenDocumentDetail}
                hasMore={hasMoreDocuments}
                isLoading={isLoadingDocuments}
                onLoadMore={loadMoreDocuments}
                onDropFiles={handleDrop}
                hiddenActions={{
                  download: true,
                  rename: false,
                  share: false,
                  assignAgent: false,
                  duplicate: false,
                  move: false,
                  delete: false
                }}
              />
            </>
          </ContentArea>
        )}

        <ShareModal
          isOpen={myWorkspacesStore.isShareModalOpen}
          store={myWorkspacesStore}
          resourcesStore={resourcesStore}
          onClose={() => myWorkspacesStore.closeShareModal()}
          onShare={handleShareAll}
          onAddToPending={handleAddToPending}
          onUpdatePendingPermission={
            myWorkspacesStore.updatePendingGranteePermission
          }
          onRemovePending={myWorkspacesStore.removePendingGrantee}
          onUpdateGranteePermission={handleUpdatePermission}
          onRemoveGrantee={handleRemoveAccess}
          usersList={myWorkspacesStore.usersStore.users}
        />

        <DuplicateModal
          isOpen={myWorkspacesStore.isDuplicateModalOpen}
          store={myWorkspacesStore}
          onClose={() => myWorkspacesStore.closeDuplicateModal()}
          onConfirm={handleDuplicateItem}
        />

        <AssignAgentModal
          isOpen={myWorkspacesStore.isAssignAgentModalOpen}
          store={myWorkspacesStore}
          onClose={() => myWorkspacesStore.closeAssignAgentModal()}
          onConfirm={handleAssignAgent}
        />

        <MoveFolderModal
          isOpen={myWorkspacesStore.isMoveModalOpen}
          onClose={() => myWorkspacesStore.closeMoveModal()}
          onSave={() => {
            myWorkspacesStore.closeMoveModal();
            toast.success({
              message: `${
                myWorkspacesStore.itemTypeBeingMoved === "folder"
                  ? "Folder"
                  : "Document"
              } moved successfully.`,
            });
          }}
          isSubmitting={false}
        />

        <DocumentDetailSlideout
          open={isDocumentDetailOpen}
          onClose={() => setIsDocumentDetailOpen(false)}
          document={selectedDocument}
          showActions
          store={myWorkspacesStore}
          onRename={handleOpenRenameDocumentModal}
          onShare={(item, type) => {
            closeSlideoutThen(() => {
              handleShareModalOpen(item, type);
            });
          }}
          onManageAccess={(doc) => {
              closeSlideoutThen(() => {
                  handleShareModalOpen(doc, 'document');
              });
          }}
          onManageAgent={(doc) => {
              closeSlideoutThen(() => {
                  handleAssignAgentModalOpen(doc);
              });
          }}
        />

        <FolderInformationSlideout
          open={isFolderInfoOpen}
          onClose={() => setIsFolderInfoOpen(false)}
          folderName={selectedFolder?.name}
          ownerName={selectedFolder?.owner_full_name || selectedFolder?.owner || 'Unknown'}
        />

        <DeleteDocumentModal
          isOpen={myWorkspacesStore.isDeleteModalOpen}
          onClose={() => myWorkspacesStore.closeDeleteModal()}
          onConfirm={handleConfirmDelete}
          documentName={myWorkspacesStore.documentToDelete?.name || ''}
          isDeleting={myWorkspacesStore.isDeletingDocument}
        />
      </MyWorkspaceContainer>
    </>
  );
};

export default observer(MyWorkspace);
